import { type FC } from 'react';

import { AppRole } from 'connect-graphql/generated';

import AppBar from '@mui/material/AppBar';

import { Box, Stack } from '@craft/ui/components/structural';
import { ThemeBreakpoints } from '@craft/ui/util';

import { HeaderLogo } from '@/src/components/atoms/logos/HeaderLogo';
import { SearchBar } from '@/src/components/organisms/SearchBar';
import { SearchBarButton } from '@/src/components/organisms/SearchBar/SearchBarButtonAndDialog';
import { HasAccess } from '@/src/features/auth/components/roles/HasAccess';
import { UserProfileDropdown } from '@/src/features/users/components/dropdowns/profile/UserProfileDropdown';

import { CraftMobileSideNav } from './CraftMobileSideNav';

/**
 * The viewport height (in pixels) below which a short landscape viewport is also treated as mobile.
 * It is a height threshold, so it has no width-breakpoint equivalent in {@link ThemeBreakpoints}.
 */
const MobileLandscapeMaxHeight = 400;

const Breaks = [
  [
    `(max-width:${ThemeBreakpoints.sm - 0.05}px)`,
    `(max-height:${MobileLandscapeMaxHeight - 0.05}px)`,
  ].join(', '),
  'and',
  `(max-width:${ThemeBreakpoints.md - 0.05}px)`,
].join(' ');

/**
 * The media query at which the mobile side navigation is shown, matching either a narrow viewport
 * or a short landscape viewport.
 *
 * This mirrors the previous viewport-measured condition so the navigation collapses into the
 * drawer in the same cases, but is driven by CSS so that it is correct on the first paint and does
 * not flash after hydration.
 */
const MobileNavMediaQuery = `@media ${Breaks}`;

/**
 * Header navigation bar for the application.
 */
export const CraftHeaderBar: FC = () => (
  <AppBar
    elevation={0}
    position='relative'
    sx={{ bgcolor: t => t.palette.secondary.dark, height: '64px', paddingX: 1.5, paddingY: 0.5 }}
  >
    <Stack
      alignItems='center'
      direction='row'
      gap={2}
      height='100%'
      justifyContent='space-between'
      maxHeight='100%'
      maxWidth='100%'
      minWidth='100%'
      sx={{ containerName: 'app-header', containerType: 'inline-size', overflow: 'hidden' }}
      width='100%'
    >
      <Stack alignItems='center' direction='row' gap={1} height='100%' paddingY={1}>
        <Box sx={{ display: 'none', [MobileNavMediaQuery]: { display: 'contents' } }}>
          <CraftMobileSideNav />
        </Box>
        <HeaderLogo />
      </Stack>
      <Stack alignItems='center' direction='row' gap={2} height='100%' sx={{ overflow: 'hidden' }}>
        <HasAccess.Any roles={[AppRole.Learner, AppRole.Evaluator]}>
          <Stack alignItems='center' direction='row' height='100%' paddingY={1}>
            <Box sx={{ display: { md: 'none', xs: 'contents' } }}>
              <SearchBarButton />
            </Box>
            <Box sx={{ display: { md: 'contents', xs: 'none' } }}>
              <SearchBar />
            </Box>
          </Stack>
        </HasAccess.Any>
        <UserProfileDropdown />
      </Stack>
    </Stack>
  </AppBar>
);
